import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import SingleCheckBoxField from './SingleCheckBoxField';

const MultiCheckBoxSelect = ({
  toggleComponent,
  options,
  value,
  drop,
  autoClose,
  align,
  name,
  displayName,
  handleChange,
  toggleClasses,
  fullWidth,
  required,
  disabled,
}) => {
  const { t } = useTranslation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});

  const handleToggleDropdown = () => setShowDropdown(!showDropdown);

  const handleCheckboxChange = (value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [value]: !prev[value],
    }));
  };

  useEffect(() => {
    const selectedValues = Object.keys(selectedOptions).filter((key) => selectedOptions[key]);
    handleChange(name, selectedValues);
  }, [selectedOptions]);

  useEffect(() => {
    if (value.length) {
      const initialSelected = value.reduce((acc, val) => {
        acc[val] = true;
        return acc;
      }, {});
      setSelectedOptions(initialSelected);
    }
  }, []);

  return (
    <div
      className={`multi-select ${fullWidth ? 'w-100' : ''} ${disabled ? 'pe-none opacity-50' : ''}`}
    >
      {displayName && (
        <label className='mb-2'>
          {t(displayName)}
          {required && <span className='lh-1 theme-size-1_5 theme-text-red-1 px-1'>*</span>}
        </label>
      )}

      <Dropdown
        show={showDropdown}
        onToggle={handleToggleDropdown}
        drop={drop ?? 'down'}
        autoClose={autoClose ?? true}
      >
        <Dropdown.Toggle
          id={`dropdown-multiselect`}
          variant='none'
          className={`d-flex justify-content-between rounded-sm cursor-pointer ${
            toggleClasses ?? ''
          }`}
          as='div'
        >
          {toggleComponent}
        </Dropdown.Toggle>
        {showDropdown && (
          <Dropdown.Menu
            className={`px-2 ${fullWidth ? 'w-100' : ''}`}
            style={{minWidth: "max-content"}}
            align={align ?? 'start'}
            as='div'
          >
            {options.map((option, index) => (
              <Fragment key={index}>
                <Dropdown.Item
                  as='div'
                  className='px-1 cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  disabled={option.disabled ?? false}
                >
                  {option.icon && <img src={option.icon} />}

                  <div className='w-100 d-flex justify-content-between align-items-center gap-3'>
                    <label>{t(option.displayValue)}</label>
                    <div className={`custom-check-option`}>
                      <SingleCheckBoxField
                        name={`${name}-${index}`}
                        value={!!selectedOptions[option.value]}
                        handleChange={() => handleCheckboxChange(option.value)}
                        classes='text-nowrap'
                        shrink
                      />
                    </div>
                  </div>
                </Dropdown.Item>
              </Fragment>
            ))}
          </Dropdown.Menu>
        )}
      </Dropdown>
    </div>
  );
};

MultiCheckBoxSelect.propTypes = {
  toggleComponent: PropTypes.object.isRequired,
  toggleClasses: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  drop: PropTypes.string,
  autoClose: PropTypes.bool,
  align: PropTypes.string,
  name: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default MultiCheckBoxSelect;
